import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { canon, comments as commentsApi, ideas as ideasApi, FeaturedIdea, Comment } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export default function FeaturedIdeaPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [idea, setIdea] = useState<FeaturedIdea | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedCanon, setExpandedCanon] = useState<string | null>(null)
  const [creatingIdea, setCreatingIdea] = useState(false)

  // Discussion
  const [forkedIdeaId, setForkedIdeaId] = useState<string | null>(null)
  const [commentList, setCommentList] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)

  useEffect(() => {
    if (!id) return
    canon.getFeaturedIdea(id)
      .then(setIdea)
      .catch(() => setError('Featured idea not found'))
      .finally(() => setLoading(false))
  }, [id])

  // Check for existing fork
  useEffect(() => {
    if (!user || !idea) return
    ideasApi.list()
      .then((userIdeas) => {
        const forked = userIdeas.find((i) => i.title === idea.title)
        if (forked) {
          setForkedIdeaId(forked.idea_id)
          commentsApi.list(forked.idea_id)
            .then(setCommentList)
            .catch(() => {})
        }
      })
      .catch(() => {})
  }, [user, idea])

  const handleSubmitComment = async () => {
    if (!forkedIdeaId || !newComment.trim()) return
    setSubmittingComment(true)
    try {
      await commentsApi.create(forkedIdeaId, newComment.trim())
      setNewComment('')
      const fresh = await commentsApi.list(forkedIdeaId)
      setCommentList(fresh)
    } catch {
      // silent
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleForkIdea = async () => {
    if (!idea) return
    setCreatingIdea(true)
    try {
      const result = await ideasApi.create({
        title: idea.title,
        raw_goal: idea.problem,
        archetype_id: (idea.goal_profile?.archetype_id as string) || undefined,
      })
      navigate(`/ideas/${result.idea_id}`)
    } catch {
      setError('Failed to fork idea. Please try again.')
      setCreatingIdea(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
          <p className="mt-3 text-gray-400 text-sm">Loading featured idea...</p>
        </div>
      </div>
    )
  }

  if (error || !idea) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-2">{error || 'Idea not found'}</p>
          <Link to="/dashboard" className="text-brand-400 hover:text-brand-300 text-sm">
            Back to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  const clarityCount = [idea.has_cb, idea.has_sp, idea.has_bp].filter(Boolean).length

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200 transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Dashboard
          </Link>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-900/30 text-amber-300 border border-amber-800/30">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            Featured Idea
          </span>
        </div>

        {/* Hero */}
        <div className="mb-8">
          <div className="flex items-start gap-4 mb-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-gray-100 mb-1">{idea.title}</h1>
              {idea.tagline && (
                <p className="text-lg text-amber-400 italic">{idea.tagline}</p>
              )}
            </div>
          </div>

          {/* Quick stats */}
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 rounded-lg border border-gray-800 text-xs">
              <svg className="w-3.5 h-3.5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-gray-400">{clarityCount}/3 canon docs</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 rounded-lg border border-gray-800 text-xs">
              <svg className="w-3.5 h-3.5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <span className="text-gray-400">{idea.collaboration_hooks.length} open questions</span>
            </div>
          </div>
        </div>

        {/* Problem & Solution */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-gray-900 rounded-xl border border-red-900/30 p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-red-900/30 flex items-center justify-center">
                <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-100">The Problem</h2>
            </div>
            <div className="prose prose-invert prose-sm max-w-none text-gray-300 leading-relaxed">
              <ReactMarkdown>{idea.problem}</ReactMarkdown>
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl border border-emerald-900/30 p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-emerald-900/30 flex items-center justify-center">
                <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-100">The Solution</h2>
            </div>
            <div className="prose prose-invert prose-sm max-w-none text-gray-300 leading-relaxed">
              <ReactMarkdown>{idea.solution}</ReactMarkdown>
            </div>
          </div>
        </div>

        {/* Collaboration Hooks */}
        {idea.collaboration_hooks.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-100 mb-2">Jump In — Open Questions</h2>
            <p className="text-sm text-gray-500 mb-4">
              Real decision points that need your input. Fork this idea and start chatting to shape the direction.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {idea.collaboration_hooks.map((hook, idx) => (
                <div
                  key={idx}
                  className="bg-gray-900 rounded-xl border border-amber-900/20 p-5 hover:border-amber-800/40 transition group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-amber-400">{idx + 1}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-gray-100 mb-1.5">{hook.question}</h3>
                      <p className="text-xs text-gray-500 mb-2 leading-relaxed">{hook.context}</p>
                      <p className="text-xs text-amber-400 font-medium group-hover:underline">{hook.invite}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Canon Preview */}
        {(idea.has_cb || idea.has_sp || idea.has_bp) && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-100 mb-4">Canon Documents</h2>
            <div className="space-y-2">
              {[
                { key: 'CB', label: 'Concept Brief', has: idea.has_cb },
                { key: 'SP', label: 'Strategic Plan', has: idea.has_sp },
                { key: 'BP', label: 'Technical Blueprint', has: idea.has_bp },
              ].map(({ key, label, has }) => {
                const content = idea.canon?.[key as keyof typeof idea.canon]
                const isExpanded = expandedCanon === key
                return (
                  <div key={key} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                    <button
                      onClick={() => setExpandedCanon(isExpanded ? null : key)}
                      className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-800/50 transition"
                      disabled={!has || !content}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          has ? 'bg-emerald-900/40 text-emerald-400' : 'bg-gray-800 text-gray-500'
                        }`}>
                          {key}
                        </div>
                        <span className="text-sm font-medium text-gray-200">{label}</span>
                        {!has && (
                          <span className="text-[10px] text-gray-600 italic">— needs your input</span>
                        )}
                      </div>
                      {has && content && (
                        <svg className={`w-4 h-4 text-gray-500 transition ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>
                    {isExpanded && content && (
                      <div className="px-5 pb-4 border-t border-gray-800 pt-3 prose prose-invert prose-sm max-w-none">
                        <ReactMarkdown>{content}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Discussion */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-100 mb-4">
            Discussion
            {commentList.length > 0 && (
              <span className="text-xs text-gray-500 ml-2">({commentList.length})</span>
            )}
          </h2>

          {forkedIdeaId ? (
            <>
              {/* Comment input */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-4 mb-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-brand-900/40 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-brand-400">
                      {user?.email?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Share your thoughts on this idea..."
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-700 bg-gray-800 text-gray-200 placeholder-gray-500 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none resize-none"
                      rows={2}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmitComment()
                      }}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] text-gray-600">Ctrl+Enter to send</span>
                      <button
                        onClick={handleSubmitComment}
                        disabled={submittingComment || !newComment.trim()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium rounded-md transition disabled:opacity-50"
                      >
                        {submittingComment ? 'Posting...' : 'Comment'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comments */}
              {commentList.length > 0 ? (
                <div className="space-y-3">
                  {commentList.map((comment) => (
                    <div key={comment.id} className="bg-gray-900 rounded-xl border border-gray-800 p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-gray-400">
                            {comment.user_email[0]?.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-gray-200">{comment.user_email}</span>
                            <span className="text-[10px] text-gray-600">{formatTimeAgo(comment.created_at)}</span>
                          </div>
                          <p className="text-sm text-gray-300 whitespace-pre-line">{comment.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-600 text-center py-4">
                  No comments yet. Be the first to share your perspective!
                </p>
              )}
            </>
          ) : (
            <div className="bg-gray-900/50 rounded-xl border border-dashed border-gray-700 p-6 text-center">
              <svg className="w-8 h-8 text-gray-700 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <p className="text-sm text-gray-400 mb-1">Fork this idea to join the discussion</p>
              <p className="text-xs text-gray-600">Comments are available once you've forked the idea.</p>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-amber-950/30 to-orange-950/30 rounded-xl border border-amber-900/30 p-8 text-center">
          <h2 className="text-xl font-bold text-gray-100 mb-2">Ready to make this yours?</h2>
          <p className="text-sm text-gray-400 mb-6 max-w-lg mx-auto">
            Fork this idea into your workspace. Chat with it, refine the canon, fill in the Blueprint,
            and run tests to validate your approach.
          </p>
          <button
            onClick={handleForkIdea}
            disabled={creatingIdea}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-3 rounded-lg text-sm transition disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {creatingIdea ? 'Creating...' : forkedIdeaId ? 'Fork Again' : 'Fork This Idea & Start Chatting'}
          </button>
          {forkedIdeaId && (
            <Link
              to={`/ideas/${forkedIdeaId}`}
              className="block mt-3 text-xs text-amber-400 hover:underline"
            >
              Open your existing fork
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
